//
// Library feed boot entry: picks the home page's feed view -- the native
// ordered list (default) or the falling "paper" scene (`?view=falling`, unless
// reduced motion is requested) -- and renders it. All the actual
// rendering logic lives in `./feed/*`:
//   - feed/list.ts    -- static list view (`renderFeed`), the shared
//                        single-card DOM builder, the `?tag=` header, and
//                        the `?view=`/reduced-motion routing decision.
//   - feed/falling.ts -- the falling scene (`renderFalling`): layout,
//                        motion loop, `visibleAlchemy`, theme/lab reapply
//                        listeners.
//   - feed/panels.ts  -- the empty/error state panel builders both views share.
//   - feed/gestures.ts -- the falling scene's pointer/wheel input state machine.
// This file is the ONLY thing pages load directly (`/app/assets/library.js`);
// esbuild's recursive module discovery (build.mjs) compiles `feed/*.ts`
// alongside it, and this module's plain relative imports (`./feed/*.js`)
// resolve them at runtime exactly like any other same-origin ES module.
//
import { renderFeed, wantsFallingView, type FeedDisposer } from "./feed/list.js";
import { renderFalling } from "./feed/falling.js";

const bootstrapView = document.querySelector<HTMLScriptElement>('script[src$="/assets/library.js"]')?.dataset.feedView;
const requestedSearch = bootstrapView === "falling" ? "?view=falling" : location.search;

let mountGeneration = 0;
let activeMount: FeedDisposer | null = null;
let mountQueue = Promise.resolve();

export function mountFeed(search: string = requestedSearch, root: ParentNode = document): Promise<void> {
  const generation = ++mountGeneration;
  const mount = async (): Promise<void> => {
    activeMount?.();
    activeMount = null;
    if (generation !== mountGeneration) return;

    const retry = (): void => void mountFeed(search, root);
    const dispose = await (wantsFallingView(search) ? renderFalling(root, retry) : renderFeed(root, retry));
    if (generation !== mountGeneration) {
      dispose();
      return;
    }
    activeMount = dispose;
  };
  mountQueue = mountQueue.then(mount, mount);
  return mountQueue;
}

export function feedMountCompletion(): Promise<void> {
  return mountQueue;
}

void mountFeed();
